import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Dropdown, Modal } from 'react-bootstrap'
import Select from 'react-select'
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import DeleteModal from 'pages/parts/DeleteModal'
import { httpOk, DELETE, GET, POST, PUT } from 'helpers/api'

// Анкета отзыва гостя: её видят по QR на чеке, в QR-меню стола и по кнопке в Telegram-боте.
// Статус вопроса: 1 — показывается гостям, 0 — отключён.
const STATUS_ACTIVE = 1
const STATUS_DISABLED = 0

const OPTIONS_MIN = 2
const OPTIONS_MAX = 20

// Типы вопросов (input_types.value) — понятным языком и с примером, а не radio/checkbox
const QUESTION_TYPES = {
	radio: { icon: 'uil-circle', label: 'rating_type_radio', hint: 'rating_type_radio_hint' },
	checkbox: { icon: 'uil-check-square', label: 'rating_type_checkbox', hint: 'rating_type_checkbox_hint' },
	selectable: { icon: 'uil-apps', label: 'rating_type_selectable', hint: 'rating_type_selectable_hint' },
	rateable: { icon: 'uil-star', label: 'rating_type_rateable', hint: 'rating_type_rateable_hint' },
	textarea: { icon: 'uil-comment-alt-lines', label: 'rating_type_textarea', hint: 'rating_type_textarea_hint' },
}
const QUESTION_TYPE_ORDER = ['rateable', 'radio', 'checkbox', 'selectable', 'textarea']

let optionKeySeq = 0
function withKey(option) {
	optionKeySeq += 1
	return { ...option, _key: `o${optionKeySeq}` }
}

const EMPTY_ITEM = {
	name: '',
	input_type_id: null,
	input_type: null,
	optionable: false,
	cheque_order_type_id: null,
	is_required: false,
	status: STATUS_ACTIVE,
	options: [],
}

// Ошибки формы: текст вопроса, пустые и повторяющиеся варианты, их количество.
// Сервер проверяет то же самое — здесь, чтобы показать ошибку у нужного поля.
function validate(item) {
	const errors = { options: {} }
	if (!String(item.name ?? '').trim()) errors.name = 'rating_err_question'
	if (!item.input_type_id) errors.type = 'rating_err_type'

	if (item.optionable) {
		const seen = {}
		;(item.options ?? []).forEach((option, index) => {
			const value = String(option.value ?? '').trim()
			if (!value) {
				errors.options[index] = 'rating_err_option_empty'
				return
			}
			const key = value.toLowerCase()
			if (seen[key] !== undefined) errors.options[index] = 'rating_err_option_duplicate'
			seen[key] = index
		})
		if ((item.options ?? []).length < OPTIONS_MIN) errors.optionsCount = 'rating_err_options_min'
	}

	const valid = !errors.name && !errors.type && !errors.optionsCount && Object.keys(errors.options).length === 0
	return { errors, valid }
}

function SortableRow({ id, disabled, children }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })

	return (
		<tr ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
			}}>
			<td className="text-center">
				<i className="uil uil-draggabledots"
					style={{ cursor: disabled ? 'default' : 'grab', opacity: disabled ? 0.3 : 1, fontSize: '18px' }}
					{...attributes} {...listeners}></i>
			</td>
			{children}
		</tr>
	)
}

function SortableOption({ id, children }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	return (
		<div ref={setNodeRef}
			className="d-flex align-items-start gap-2 mb-2"
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
			}}>
			<i className="uil uil-draggabledots mt-2" style={{ cursor: 'grab', fontSize: '18px' }}
				{...attributes} {...listeners}></i>
			{children}
		</div>
	)
}

function Index() {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [inputs, setInputs] = useState([])
	const [orderTypes, setOrderTypes] = useState([])
	const [selectedItem, setSelectedItem] = useState(EMPTY_ITEM)
	const [showErrors, setShowErrors] = useState(false)
	const [showModal, setShowModal] = useState(false)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteId, setDeleteId] = useState(null)

	// 6px — чтобы клик по строке не превращался в перетаскивание
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

	const orderTypeOptions = [{ id: null, name: t('rating_all_order_types') }, ...orderTypes]
	const { errors, valid } = validate(selectedItem)

	function typeOf(item) {
		return QUESTION_TYPES[item?.input_type] ?? null
	}

	function typeLabel(item) {
		const type = typeOf(item)
		return type ? t(type.label) : (item?.input_type ?? '')
	}

	function orderTypeName(id) {
		if (!id) return t('rating_all_order_types')
		return orderTypes.find(item => item.id === id)?.name ?? ''
	}

	async function getData() {
		const response = await GET('/service-rating-templates', {}, { loader: true })
		if (httpOk(response)) setData(response.data?.data ?? [])
	}

	async function getInputTypes() {
		const response = await GET('/helper/input-types')
		if (httpOk(response)) setInputs(response.data ?? [])
	}

	async function getOrderTypes() {
		const response = await GET('/helper/order-types')
		if (httpOk(response)) setOrderTypes(response.data ?? [])
	}

	function toggleModal(bool, item = EMPTY_ITEM) {
		const copy = JSON.parse(JSON.stringify(item))
		copy.options = (copy.options ?? []).map(withKey)
		copy.status = copy.status === STATUS_DISABLED ? STATUS_DISABLED : STATUS_ACTIVE
		copy.is_required = Boolean(copy.is_required)
		setSelectedItem(copy)
		setShowErrors(false)
		setShowModal(bool)
	}

	function selectType(input) {
		if (selectedItem.id) return
		const next = { ...selectedItem, input_type_id: input.id, input_type: input.name, optionable: Boolean(input.optionable) }
		// Пустая анкета-оценка сразу получает шкалу 1–5, чтобы не набивать цифры руками
		if (input.name === 'rateable' && next.options.length === 0) {
			next.options = ['1', '2', '3', '4', '5'].map(value => withKey({ value }))
		}
		if (input.optionable && next.options.length === 0) {
			next.options = [withKey({ value: '' }), withKey({ value: '' })]
		}
		setSelectedItem(next)
	}

	function changeOption(index, value) {
		const options = [...selectedItem.options]
		options[index] = { ...options[index], value }
		setSelectedItem({ ...selectedItem, options })
	}

	function addOption() {
		if (selectedItem.options.length >= OPTIONS_MAX) return
		setSelectedItem({ ...selectedItem, options: [...selectedItem.options, withKey({ value: '' })] })
	}

	function removeOption(index) {
		const options = [...selectedItem.options]
		options.splice(index, 1)
		setSelectedItem({ ...selectedItem, options })
	}

	function fillScale() {
		setSelectedItem({ ...selectedItem, options: ['1', '2', '3', '4', '5'].map(value => withKey({ value })) })
	}

	function handleOptionKeyDown(e, index) {
		// Enter в последнем варианте — следующий вариант, как в списке дел
		if (e.key === 'Enter') {
			e.preventDefault()
			if (index === selectedItem.options.length - 1) addOption()
		}
	}

	function handleOptionsDragEnd(event) {
		const { active, over } = event
		if (!over || active.id === over.id) return
		const oldIndex = selectedItem.options.findIndex(item => item._key === active.id)
		const newIndex = selectedItem.options.findIndex(item => item._key === over.id)
		if (oldIndex === -1 || newIndex === -1) return
		setSelectedItem({ ...selectedItem, options: arrayMove(selectedItem.options, oldIndex, newIndex) })
	}

	async function createUpdate(e) {
		e.preventDefault()
		setShowErrors(true)
		if (!valid) return

		const payload = {
			id: selectedItem.id,
			name: selectedItem.name.trim(),
			input_type_id: selectedItem.input_type_id,
			cheque_order_type_id: selectedItem.cheque_order_type_id ?? null,
			is_required: selectedItem.is_required,
			status: selectedItem.status,
			options: selectedItem.optionable
				? selectedItem.options.map(option => ({ id: option.id ?? null, value: String(option.value).trim() }))
				: [],
		}

		const response = selectedItem.id
			? await PUT('/service-rating-templates', payload, { loader: true })
			: await POST('/service-rating-templates', payload, { loader: true })

		if (httpOk(response)) {
			getData()
			setShowModal(false)
		}
	}

	async function toggleStatus(item) {
		const status = item.status === STATUS_ACTIVE ? STATUS_DISABLED : STATUS_ACTIVE
		setData(data.map(row => row.id === item.id ? { ...row, status } : row))
		const response = await PUT('/service-rating-templates/status', { id: item.id, status }, { notification: false })
		if (!httpOk(response)) getData()
	}

	async function handleDragEnd(event) {
		const { active, over } = event
		if (!over || active.id === over.id) return

		const oldIndex = data.findIndex(item => item.id === active.id)
		const newIndex = data.findIndex(item => item.id === over.id)
		if (oldIndex === -1 || newIndex === -1) return

		const reordered = arrayMove(data, oldIndex, newIndex)
		setData(reordered)

		const response = await PUT('/service-rating-templates/order', {
			ids: reordered.map(item => item.id),
		}, { notification: false })

		// не сохранилось — возвращаем порядок с сервера, чтобы экран не врал
		if (!httpOk(response)) getData()
	}

	async function deleteItem() {
		const response = await DELETE(`/service-rating-templates?id=${deleteId}`, {}, { loader: true })
		if (httpOk(response)) {
			getData()
			setModalDelete(false)
		}
	}

	useEffect(() => {
		getData()
		getInputTypes()
		getOrderTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const sortedInputs = [...inputs].sort((a, b) => QUESTION_TYPE_ORDER.indexOf(a.name) - QUESTION_TYPE_ORDER.indexOf(b.name))

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('service_rating_templates')} [{data.length}]
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('create')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="text-muted mb-3">
						<i className="uil uil-info-circle me-1"></i>
						{t('rating_page_hint')}
					</div>

					{data.length === 0 ?
						<div className="text-center py-5">
							<i className="uil uil-comment-question" style={{ fontSize: '48px', color: '#9aa0ac' }}></i>
							<div className="mt-2 mb-3">{t('rating_empty')}</div>
							<button className="btn btn-primary" onClick={() => toggleModal(true)}>{t('rating_add_question')}</button>
						</div>
						:
						<div className="table-responsive">
							<table className="table table-bordered table-hover mb-0">
								<thead>
									<tr>
										<th style={{ 'width': '33px', 'minWidth': '33px' }}></th>
										<th style={{ 'width': '33px', 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '34%' }}>{t('question')}</th>
										<th style={{ 'width': '16%' }}>{t('rating_question_type')}</th>
										<th style={{ 'width': '12%' }}>{t('order_type')}</th>
										<th style={{ 'width': '28%' }}>{t('rating_options')}</th>
										<th style={{ 'width': '10%' }} className="text-center">{t('rating_active')}</th>
										<th style={{ 'width': '33px', 'minWidth': '33px' }}></th>
									</tr>
								</thead>
								<tbody>
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
										<SortableContext items={data.map(item => item.id)} strategy={verticalListSortingStrategy}>
											{data.map((item, index) => (
												<SortableRow key={item.id} id={item.id}>
													<td className="text-center">{index + 1}</td>
													<td className="cursor" onClick={() => toggleModal(true, item)}
														style={{ opacity: item.status === STATUS_ACTIVE ? 1 : 0.5 }}>
														{item.name}
														{item.is_required &&
															<span className="text-danger ms-1" title={t('rating_required')}>*</span>
														}
													</td>
													<td>
														<i className={`uil ${typeOf(item)?.icon ?? 'uil-question'} me-1`}></i>
														{typeLabel(item)}
													</td>
													<td>{orderTypeName(item.cheque_order_type_id)}</td>
													<td>
														<div className="d-flex flex-wrap gap-1">
															{(item.options ?? []).map((option) => (
																<span key={option.id} className="badge bg-light text-dark border fw-normal">{option.value}</span>
															))}
														</div>
													</td>
													<td className="text-center">
														<div className="form-check form-switch d-inline-block mb-0">
															<input type="checkbox" className="form-check-input"
																checked={item.status === STATUS_ACTIVE}
																onChange={() => toggleStatus(item)} />
														</div>
													</td>
													<td>
														<div className="table-dropdown">
															<Dropdown id={`dropdown-${index}`}>
																<Dropdown.Toggle variant="outline-primary">
																	<i className="uil uil-ellipsis-v"></i>
																</Dropdown.Toggle>

																<Dropdown.Menu>
																	<div className="table-dropdown-item"
																		onClick={() => toggleModal(true, item)}>
																		<i className="uil-edit-alt"></i>
																		{t('edit')}
																	</div>
																	<div className="table-dropdown-item text-danger"
																		onClick={() => {
																			setDeleteId(item.id)
																			setModalDelete(true)
																		}}>
																		<i className="uil-times"></i>
																		{t('delete')}
																	</div>
																</Dropdown.Menu>
															</Dropdown>
														</div>
													</td>
												</SortableRow>
											))}
										</SortableContext>
									</DndContext>
								</tbody>
							</table>
						</div>
					}
					{data.length > 1 &&
						<small className="text-muted d-block mt-2">{t('rating_drag_hint')}</small>
					}
				</div>
			</div>

			{/* MODAL CREATE UPDATE */}
			<Modal show={showModal} animation={false} centered scrollable size="lg"
				backdrop="static"
				onHide={() => setShowModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedItem.id ? t('edit') : t('rating_add_question')}
					</Modal.Title>
				</Modal.Header>
				<form onSubmit={createUpdate} autoComplete="off" style={{ display: 'contents' }}>
					<Modal.Body style={{ height: '560px' }}>
						<div className="form-group">
							<label>{t('rating_question_type')}</label>
							<div className="row g-2">
								{sortedInputs.map((input) => {
									const meta = QUESTION_TYPES[input.name]
									const active = selectedItem.input_type_id === input.id
									const locked = Boolean(selectedItem.id) && !active
									return (
										<div className="col-md-6" key={input.id}>
											<div className={`card mb-0 h-100 ${active ? 'border-primary' : ''}`}
												style={{
													cursor: selectedItem.id ? 'default' : 'pointer',
													opacity: locked ? 0.45 : 1,
													borderWidth: active ? '2px' : '1px',
												}}
												onClick={() => selectType(input)}>
												<div className="card-body p-2 d-flex gap-2">
													<i className={`uil ${meta?.icon ?? 'uil-question'}`}
														style={{ fontSize: '22px', color: active ? 'var(--bs-primary)' : '#9aa0ac' }}></i>
													<div>
														<div className="fw-bold">{meta ? t(meta.label) : input.name}</div>
														<small className="text-muted">{meta ? t(meta.hint) : ''}</small>
													</div>
												</div>
											</div>
										</div>
									)
								})}
							</div>
							{selectedItem.id &&
								<small className="text-muted d-block mt-1">{t('rating_type_locked')}</small>
							}
							{showErrors && errors.type &&
								<small className="text-danger d-block mt-1">{t(errors.type)}</small>
							}
						</div>

						<div className="form-group">
							<label>{t('question')}</label>
							<textarea className={`form-control ${showErrors && errors.name ? 'is-invalid' : ''}`}
								rows={2}
								maxLength={255}
								style={{ resize: 'none' }}
								placeholder={t('rating_question_placeholder')}
								value={selectedItem.name ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })} />
							<div className="d-flex justify-content-between">
								<small className="text-danger">{showErrors && errors.name ? t(errors.name) : ''}</small>
								<small className="text-muted">{String(selectedItem.name ?? '').length}/255</small>
							</div>
						</div>

						<div className="row">
							<div className="form-group col-md-6">
								<label>{t('order_type')}</label>
								<Select
									options={orderTypeOptions}
									value={orderTypeOptions.find(option => option.id === (selectedItem.cheque_order_type_id ?? null))}
									onChange={(option) => setSelectedItem({ ...selectedItem, cheque_order_type_id: option?.id ?? null })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => String(option.id)}
								/>
								<small className="text-muted">{t('rating_order_type_hint')}</small>
							</div>
							<div className="form-group col-md-6">
								<div className="form-check form-switch mb-1 mt-4">
									<input id="rating_required" type="checkbox" className="form-check-input"
										checked={selectedItem.is_required}
										onChange={(e) => setSelectedItem({ ...selectedItem, is_required: e.target.checked })} />
									<label className="form-check-label" htmlFor="rating_required">{t('rating_required')}</label>
								</div>
								<small className="text-muted d-block mb-2">{t('rating_required_hint')}</small>
								<div className="form-check form-switch mb-0">
									<input id="rating_active" type="checkbox" className="form-check-input"
										checked={selectedItem.status === STATUS_ACTIVE}
										onChange={(e) => setSelectedItem({ ...selectedItem, status: e.target.checked ? STATUS_ACTIVE : STATUS_DISABLED })} />
									<label className="form-check-label" htmlFor="rating_active">{t('rating_active')}</label>
								</div>
							</div>
						</div>

						{selectedItem.optionable &&
							<div className="form-group">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<label className="mb-0">{t('rating_options')}</label>
									{selectedItem.input_type === 'rateable' &&
										<button type="button" className="btn btn-sm btn-outline-primary" onClick={fillScale}>
											{t('rating_fill_scale')}
										</button>
									}
								</div>

								<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOptionsDragEnd}>
									<SortableContext items={selectedItem.options.map(option => option._key)} strategy={verticalListSortingStrategy}>
										{selectedItem.options.map((option, index) => (
											<SortableOption key={option._key} id={option._key}>
												<div className="w-100">
													<input type="text"
														className={`form-control ${showErrors && errors.options[index] ? 'is-invalid' : ''}`}
														maxLength={255}
														placeholder={t('rating_option_placeholder', { n: index + 1 })}
														value={option.value ?? ''}
														onKeyDown={(e) => handleOptionKeyDown(e, index)}
														onChange={(e) => changeOption(index, e.target.value)} />
													{showErrors && errors.options[index] &&
														<small className="text-danger">{t(errors.options[index])}</small>
													}
												</div>
												<div className="table-action-button table-action-danger-button mt-1"
													onClick={() => removeOption(index)}>
													<i className="uil-times"></i>
												</div>
											</SortableOption>
										))}
									</SortableContext>
								</DndContext>

								<button type="button" className="btn btn-outline-primary w-100"
									disabled={selectedItem.options.length >= OPTIONS_MAX}
									onClick={addOption}>
									<i className="uil uil-plus me-1"></i>{t('rating_add_option')}
								</button>
								{showErrors && errors.optionsCount &&
									<small className="text-danger d-block mt-1">{t(errors.optionsCount)}</small>
								}
							</div>
						}

						{selectedItem.input_type_id &&
							<div className="form-group mb-0">
								<label>{t('rating_preview')}</label>
								<div className="border rounded p-3 bg-light">
									<div className="fw-bold mb-2">
										{selectedItem.name?.trim() || t('rating_question_placeholder')}
										{selectedItem.is_required && <span className="text-danger ms-1">*</span>}
									</div>
									{selectedItem.input_type === 'textarea' &&
										<div className="form-control" style={{ height: '60px', background: '#fff' }}></div>
									}
									{(selectedItem.input_type === 'rateable' || selectedItem.input_type === 'selectable') &&
										<div className="d-flex flex-wrap gap-2">
											{selectedItem.options.filter(option => String(option.value).trim()).map(option => (
												<span key={option._key} className="border rounded-pill px-3 py-1 bg-white">
													{selectedItem.input_type === 'rateable' && <i className="uil uil-star me-1"></i>}
													{option.value}
												</span>
											))}
										</div>
									}
									{(selectedItem.input_type === 'radio' || selectedItem.input_type === 'checkbox') &&
										selectedItem.options.filter(option => String(option.value).trim()).map(option => (
											<div key={option._key} className="d-flex align-items-center gap-2 mb-1">
												<span className="border bg-white d-inline-block"
													style={{ width: 16, height: 16, borderRadius: selectedItem.input_type === 'radio' ? '50%' : '4px' }}></span>
												{option.value}
											</div>
										))
									}
								</div>
							</div>
						}
					</Modal.Body>
					<Modal.Footer>
						<div className="d-flex gap-2 w-100">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => setShowModal(false)}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100">{t('save')}</button>
						</div>
					</Modal.Footer>
				</form>
			</Modal>
			{/* MODAL CREATE UPDATE */}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>
		</>
	)
}

export default Index
